import {
	DataHandlerDevice,
	DataHandlerDeviceAdmin,
	Device,
	DeviceSpotlightManager,
} from "luxedo-data"
import { DeviceApi, DeviceLicense } from "@devices/api-client"

import { LuxedoRPC } from "luxedo-rpc"
import { Toast } from "toaster"
import { get, writable } from "svelte/store"

type NewDeviceInputs_Auto = {
	automatic: true
	license: DeviceLicense
}

type NewDeviceInputs_Manual = {
	automatic: false
	license: DeviceLicense
	id: number
}

export type NewDeviceInputs = NewDeviceInputs_Auto | NewDeviceInputs_Manual

export namespace TestStation {
	export type ContextType = {
		activeDevice?: Device
		newID?: number
		newPass?: string
		processingNewDevice?: boolean
	}

	const OFF_STATUSES = ["OFF", "DORMANT", "LOST", "NEW"]
	const TEMPLATE_ID = 4
	const STORE_DEFAULT = {
		activeDevice: undefined,
		processingNewDevice: false,
	}

	const store = writable<ContextType>({})
	const devicesApi = new DeviceApi(`${import.meta.env.VITE_API_URL}/api`)

	export async function initialize() {
		await waitForDevice(TEMPLATE_ID)
		Toast.success("Connected to template device!")
		DataHandlerDeviceAdmin.pull([TEMPLATE_ID])
	}

	// #region    =========================== Store Management ===========================

	export function subscribe(cb: (ctx: ContextType) => void) {
		return store.subscribe(cb)
	}

	export function reset(clearSearch = true) {
		if (interval) clearInterval(interval)

		// to clear the device search bar
		if (clearSearch) {
			const searchInput = document.getElementById("search-input") as HTMLInputElement
			if (searchInput) searchInput.value = ""
		}

		store.set(STORE_DEFAULT)
	}

	export function setActiveDevice(device: Device) {
		store.update((ctx) => ({
			...ctx,
			activeDevice: device,
			processingNewDevice: false,
		}))
	}

	// #endregion =========================== Store Management ===========================
	// #region    =========================== Device Connection ===========================

	let interval
	/**
	 * Waits for the specified device to come online. Once online, this will resolve and return true.
	 * @param devID the device id to connect to
	 * @returns a promise which will resolve once connected to the device
	 */
	export async function waitForDevice(devID: number) {
		if (interval) clearInterval(interval)

		return new Promise<void>((res) => {
			const resolve = () => {
				clearInterval(interval)
				return res()
			}

			interval = setInterval(async () => {
				const didConnect = await connectToDevice(devID)

				console.warn(`attempting connection to ${devID}`, didConnect)

				if (didConnect) resolve()
			}, 5000)
		})
	}

	/**
	 * "Connects" to a device. Calls `device_control_aquire` and sets the active device in the store to said device.
	 * @param devID the device id to connect to
	 * @returns true if connection was successful, false otherwise
	 */
	async function connectToDevice(devID: number): Promise<boolean> {
		try {
			await DataHandlerDeviceAdmin.pull([devID])
			const newStatus = await LuxedoRPC.api.admin.device_control_aquire(devID, 1)

			if (checkIsDeviceOnline(newStatus.current_status)) {
				const device = DataHandlerDeviceAdmin.get(devID)

				await DataHandlerDevice.pull([devID])
				DeviceSpotlightManager.initialize()

				setActiveDevice(device)

				return true
			}
		} catch (e) {
			console.warn(`Unable to connect to device with ID: ${devID}.`)
		}

		return false
	}

	async function setLicense(id: number, license: DeviceLicense) {
		await devicesApi.setDeviceLicense(id, license)
	}

	/**
	 * Assigns a new ID and password to the currently active device, then attempts to reconnect to it.
	 */
	export async function assignAndConnect(inputs: NewDeviceInputs) {
		let id: number
		let password: string

		if (inputs.automatic === false) {
			const newDeviceData = await assignOldId(inputs.id)
			id = newDeviceData.id
			password = newDeviceData.password
		} else {
			const newDeviceData = await assignNewID()
			id = newDeviceData.id
			password = newDeviceData.password
		}

		await setLicense(id, inputs.license)

		store.update((ctx) => ({
			...ctx,
			activeDevice: undefined,
			newID: id,
			newPass: password,
			processingNewDevice: true,
		}))

		await waitForDevice(id)
	}

	/**
	 * Assigns a new ID and password to the currently active device.
	 * @returns {id: string, password: string} The new ID and password
	 */
	async function assignNewID() {
		let device = get(store).activeDevice
		if (!device) throw new Error("Not connected to any device...")
		const currentStatus = await LuxedoRPC.api.admin.device_control_aquire(device.id, 1)
		if (!checkIsDeviceOnline(currentStatus.current_status)) {
			Toast.error("Template device is no longer online, attempting to reconnect...")
			reset()
		}

		const { new_dev_id, new_dev_password } = await LuxedoRPC.api.admin.line_setup_for_production(
			device.id,
			6,
		)

		return {
			id: new_dev_id,
			password: new_dev_password,
		}
	}

	async function assignOldId(oldId: number) {
		let device = get(store).activeDevice
		if (!device) throw new Error("Not connected to any device...")
		const currentStatus = await LuxedoRPC.api.admin.device_control_aquire(device.id, 1)
		if (!checkIsDeviceOnline(currentStatus.current_status)) {
			Toast.error("Device is no longer online, attempting to reconnect...")
			reset()
		}

		const { new_dev_id, new_dev_password } = await LuxedoRPC.api.admin.line_setup_reuse_device(
			device.id,
			oldId,
		)

		return {
			id: new_dev_id,
			password: new_dev_password,
		}
	}

	// #endregion =========================== Device Connection ===========================
	// #region    =========================== Utilities ===========================

	/**
	 * Compares the provided status with a list of OFF statuses, returning true if the status is not an offline status.
	 * @param status the status to test
	 * @returns false if the status is an offline status, true otherwise
	 */
	function checkIsDeviceOnline(status: string) {
		return !OFF_STATUSES.includes(status)
	}

	// #endregion =========================== Utilities ===========================
}
